import { Dialog } from '@flashka/shared/ui';

import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { AccountProfileTab } from './account-profile-tab';
import { AccountSecurityTab } from './account-security-tab';

/**
 * The account-management dialog behind `useAuthControls().openUserProfile()`
 * — two tabs (Profile / Security), mounted once in `AuthDialogs`. Both tabs
 * render regardless of session state; the security tab shows a skeleton
 * mirroring its loaded layout while its list fetches resolve, and the
 * profile tab returns `null` until `useSession()` has a user (already
 * resolved in practice — the dialog opens from signed-in UI), so there's
 * no dialog-level loading gate here.
 *
 * `data-account-dialog` marks the body for the settings modal, which opens
 * this one on top of itself and must not treat a dismissal while this is
 * mounted as its own (see settings-modal-context.tsx).
 */
export function AccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      size="md"
      title={t('auth.profile.title')}
      closeAccessibilityLabel={t('a11y.close')}
      bodyScroll>
      <div data-account-dialog className="flex w-full flex-col">
        <Tabs defaultValue="profile">
          <TabsList className="w-full">
            <TabsTrigger value="profile" className="flex-1">
              {t('auth.profile.tabProfile')}
            </TabsTrigger>
            <TabsTrigger value="security" className="flex-1">
              {t('auth.profile.tabSecurity')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="profile">
            <AccountProfileTab />
          </TabsContent>
          <TabsContent value="security">
            <AccountSecurityTab />
          </TabsContent>
        </Tabs>
      </div>
    </Dialog>
  );
}
